import { getScorePercentage } from './score-percentage-policy';
import type { ISimulationScore, TNormalizedRatingCutoffs } from '@/types';

// ================================
// Types
// ================================

/**
 * Domain-level shape for score history card entries.
 *
 * Why this type exists:
 * - It gives the scoring domain a stable, UI-compatible output contract.
 * - It allows the feature hook to stay focused on orchestration (store/routing)
 *   while this module owns the data transformation policy.
 * - It decouples domain policy tests from component-level type imports.
 *
 * Compatibility note:
 * - This shape intentionally mirrors what score history card consumers expect.
 * - The field names and fallback values below should only be changed with care,
 *   because small differences can alter rendered history cards and snapshots.
 */
export type TScoreHistoryEntry = {
  /** ISO string or Date object as provided by upstream score source. */
  date: Date | string;

  /** Stable identifier for rendering keyed lists. */
  id: string;

  /** Maximum score denominator used by score display components. */
  maxScore: number;

  /** Optional pass/fail indicator for cert/practice contexts. */
  passed?: boolean;

  /** Optional runtime L1-L4 percentage cutoffs from the score payload. */
  runtimeRatingCutoffs?: TNormalizedRatingCutoffs;

  /** Raw achieved score (numerator). */
  score: number;

  /** Normalized/derived percentage value for history row visualizations. */
  scorePercentage: number;
};

/**
 * Input contract for score history derivation.
 *
 * Design notes:
 * - `simulationId` is nullable because several feature props model route params
 *   as `string | null` before navigation context is fully resolved.
 * - `currentDateCompleted` and `currentSessionId` are optional to preserve the
 *   existing behavior where missing values broaden/narrow filters naturally.
 */
type BuildScoreHistoryEntriesParams = {
  /** Completion timestamp of the currently viewed scorecard. */
  currentDateCompleted?: Date | string;

  /** Runtime L1-L4 cutoffs from the currently viewed scorecard. */
  currentRuntimeRatingCutoffs?: TNormalizedRatingCutoffs;

  /** Session id of the currently viewed scorecard, used for exclusion. */
  currentSessionId?: number;

  /** Full score collection from store/state to derive history from. */
  scores: ISimulationScore[];

  /** Current simulation identifier used to scope history. */
  simulationId?: string | null;
};

type SortScoreHistoryEntriesParams = {
  entries: TScoreHistoryEntry[];
};

// ================================
// Public Domain Policy
// ================================

/**
 * Builds score history entries for a simulation scorecard context.
 *
 * This function preserves pre-refactor feature behavior exactly.
 *
 * Rule set (in execution order):
 * 1. Restrict to scores that belong to the active simulation.
 * 2. Remove the currently viewed session from history results.
 * 3. Exclude scores where `score` is undefined (incomplete/unscored records).
 * 4. Keep only scores completed on/before the active score completion date.
 * 5. Map to a normalized history-entry shape with existing fallback defaults.
 *
 * Why order matters:
 * - Earlier filters reduce data volume before later comparisons/mapping.
 * - The current-session exclusion happens before mapping so list indices for
 *   fallback ids match legacy behavior for remaining entries.
 *
 * Fallback policy (parity-safe with prior hook implementation):
 * - `id`: `entry.uuid` or `score-history-entry-{index}`
 * - `score`: `entry.score` or `0`
 * - `maxScore`: `entry.scoreMax` or `4`
 * - `scorePercentage`: `entry.scorePercentage` or `0`
 *
 * Edge-case behavior intentionally retained:
 * - Missing `simulationId` returns `[]` immediately.
 * - Missing `currentDateCompleted` uses empty-string comparison baseline.
 * - Date comparison uses legacy loose strategy to avoid behavior drift.
 */
export const buildScoreHistoryEntries = ({
  currentDateCompleted,
  currentRuntimeRatingCutoffs,
  currentSessionId,
  scores,
  simulationId,
}: BuildScoreHistoryEntriesParams): TScoreHistoryEntry[] => {
  // Without a simulation context, there is no valid history scope.
  // Returning an empty array keeps callers deterministic and null-safe.
  if (!simulationId) return [];

  // Filtering is the first step to narrow down the score set before applying mapping
  // and sorting transformations. This preserves the original intent of each rule
  // and ensures that mapping/sorting logic only runs on relevant entries.
  const eligibleScores = filterEligibleScoreHistoryEntries({
    scores,
    currentDateCompleted,
    currentSessionId,
    simulationId,
  });

  // Mapping is a pure transformation that applies the defined shape and fallback
  // policy to each entry, without affecting eligibility or sort order.
  const mappedEntries = mapScoreHistoryEntries(
    eligibleScores,
    currentRuntimeRatingCutoffs,
  );

  // Sorting is a pure transformation that can be applied after mapping without
  // affecting the shape or fallback policies of individual entries.
  const sortedEntries = sortScoreHistoryEntries({ entries: mappedEntries });

  // The final output is a sorted array of history entries that conform to the defined
  // shape and eligibility rules, ready for consumption by score history cards.
  return sortedEntries;
};

/**
 * Filters raw simulation scores to determine eligibility for score history display.
 *
 * This function applies the defined rule set to the input scores and returns a
 * subset that meets all criteria for inclusion in score history.
 */
export const filterEligibleScoreHistoryEntries = ({
  scores,
  currentDateCompleted,
  currentSessionId,
  simulationId,
}: BuildScoreHistoryEntriesParams) => {
  // Preserve legacy fallback behavior from the feature hook:
  // when no current completion date is available, use an empty string so the
  // downstream comparison semantics stay exactly the same as before extraction.
  const completedBy = currentDateCompleted || '';

  // Transformation pipeline:
  // 1) scope by simulation
  // 2) remove active session
  // 3) ensure score exists
  // 4) apply completion date cutoff
  // 5) normalize shape + defaults
  const eligibleScores = scores
    // Keep scores for the same simulation only.
    .filter((score) => score.simulationId === simulationId)

    // Exclude the currently viewed score attempt to avoid duplicate current row.
    .filter((score) => score.sessionId !== currentSessionId)

    // Ignore score records that are not numerically populated yet.
    .filter((score) => score.score !== undefined)

    // Preserve historical comparison behavior used in feature hook.
    // This intentionally keeps loose comparison semantics to avoid introducing
    // subtle ordering/eligibility regressions during refactor.
    .filter((score) => (score.dateCompleted as any) <= (completedBy as any));

  // At this point, `eligibleScores` is a filtered array of `ISimulationScore`
  // objects that meet the defined criteria.
  return eligibleScores;
};

/**
 * Maps raw simulation scores to normalized score history entries.
 *
 * This function applies a consistent shape and fallback policy to transform
 * raw score data into the format expected by score history card consumers.
 */
export const mapScoreHistoryEntries = (
  entries: ISimulationScore[],
  simulationRuntimeRatingCutoffs?: TNormalizedRatingCutoffs,
): TScoreHistoryEntry[] => {
  return entries.map((entry, index) => {
    return {
      // Keep original date payload type (Date|string) for caller flexibility.
      date: entry.dateCompleted,

      // Fallback id ensures stable keying even when upstream uuid is missing.
      id: entry.uuid || `score-history-entry-${index}`,

      // Default `maxScore` to 4 to match prior scorecard assumptions.
      maxScore: entry.scoreMax || 4,

      // Optional pass field is forwarded directly when present.
      passed: entry.pass,
      runtimeRatingCutoffs:
        simulationRuntimeRatingCutoffs || entry.runtimeRatingCutoffs,

      // Keep numeric defaults aligned with legacy hook behavior.
      score: entry.score || 0,
      scorePercentage: getScorePercentage(entry) || 0,
    };
  });
};

/**
 * Sorts score history entries from most recent to oldest.
 *
 * Policy guarantees:
 * - Returns a new array reference.
 * - Does not mutate the original `entries` input.
 * - Uses descending date order to match score-history card behavior.
 */
export const sortScoreHistoryEntries = ({
  entries,
}: SortScoreHistoryEntriesParams): TScoreHistoryEntry[] => {
  return [...entries].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
};
